import React from 'react';
import { Chip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { usePagedList } from '../../../../hooks/use-paged-list';
import { dataGridDefaults } from '../../../../components/list-grid-panel';
import { RowAction } from '../../../../components/row-action';
import { EditAction, DeleteAction, OpenLinkAction } from '../../../../components/row-actions';
import { TwoLineCell } from '../../../../components/two-line-cell';
import { IContractPagedResponse } from '../../../../model/scp/contract/response/contract-paged-response';
import { IContractPageRequest } from '../../../../model/scp/contract/request/contract-paged-request';

type ChipColor = 'primary' | 'secondary' | 'warning' | 'error' | 'success' | 'default';

const CHIP_COLOR_BY_ID: Record<number, ChipColor> = { 1: 'primary', 2: 'secondary', 3: 'warning', 4: 'error', 5: 'success', 6: 'default' };

function LookupChip({ label, id }: { label?: string; id: number }) {
    return label ? <Chip size="small" label={label} color={CHIP_COLOR_BY_ID[id] || 'primary'} /> : null;
}

export interface ContractDataTableProps {
    parameters: IContractPageRequest;
    searchTrigger: number;
    onPaginationChange: (page: number, pageSize: number) => void;
    onEdit: (row: IContractPagedResponse) => void;
    onAmendments: (row: IContractPagedResponse) => void;
    onDelete: (row: IContractPagedResponse, onDeleted: () => void) => void;
}

export default function ContractDataTable({ parameters, searchTrigger, onPaginationChange, onEdit, onAmendments, onDelete }: ContractDataTableProps) {
    const { rows, rowCount, loading, reload } = usePagedList(ContractAppService.listContract, parameters, searchTrigger, 'Não foi possível carregar os contratos');

    const columns: GridColDef<IContractPagedResponse>[] = [
        { field: 'id', headerName: 'Id', width: 90, sortable: false },
        {
            field: 'contractEffortNumber', headerName: 'Nº Empenho', minWidth: 160, flex: 0.7, sortable: false,
            renderCell: (params) => <TwoLineCell primary={params.value} secondary={params.row.numberProcess ? `Processo ${params.row.numberProcess}` : undefined} bold />
        },
        { field: 'year', headerName: 'Exercício', width: 100, sortable: false },
        { field: 'supplierName', headerName: 'Fornecedor', flex: 1.5, minWidth: 200, sortable: false },
        {
            field: 'contractType', headerName: 'Tipo de contrato', flex: 1, minWidth: 160, sortable: false,
            renderCell: (params) => <LookupChip label={params.value} id={params.row.contractTypeId} />
        },
        {
            field: 'processCategory', headerName: 'Categoria do processo', flex: 1, minWidth: 180, sortable: false,
            renderCell: (params) => <LookupChip label={params.value} id={params.row.categoryProcessId} />
        },
        {
            field: 'actions', headerName: 'Ações', width: 160, sortable: false, filterable: false,
            renderCell: (params) => (
                <React.Fragment>
                    {params.row.url && <OpenLinkAction url={params.row.url} title="Acessar no portal" />}
                    <RowAction title="Aditivos" onClick={() => onAmendments(params.row)}>
                        <PostAddIcon fontSize="small" />
                    </RowAction>
                    <EditAction onClick={() => onEdit(params.row)} />
                    <DeleteAction onClick={() => onDelete(params.row, reload)} />
                </React.Fragment>
            )
        }
    ];

    return (
        <DataGrid
            {...dataGridDefaults}
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            rowHeight={60}
            paginationMode="server"
            paginationModel={{ page: parameters.page ?? 0, pageSize: parameters.pageSize ?? 100 }}
            onPaginationModelChange={(model) => onPaginationChange(model.page, model.pageSize)}
        />
    );
}
